import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnhancedLeaderboard = ({ user }) => {
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('all'); // all, month, week
  const [category, setCategory] = useState('points'); // points, achievements, streak

  useEffect(() => {
    if (user) {
      loadLeaderboard();
    }
  }, [user, timeRange, category]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('session_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const response = await axios.get(`${API}/achievements/leaderboard?limit=50`, { headers });
      setLeaderboardData(response.data);
      
    } catch (err) {
      console.error('Error loading leaderboard:', err);
      setError('Failed to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTierInfo = (rank) => {
    if (rank === 1) return { name: 'Champion', color: 'from-yellow-400 to-yellow-600', icon: '👑' };
    if (rank <= 3) return { name: 'Elite', color: 'from-purple-400 to-purple-600', icon: '💎' };
    if (rank <= 10) return { name: 'Master', color: 'from-blue-400 to-blue-600', icon: '🏆' };
    if (rank <= 25) return { name: 'Expert', color: 'from-green-400 to-green-600', icon: '⭐' };
    return { name: 'Rising', color: 'from-gray-400 to-gray-600', icon: '🚀' };
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getAchievementBadge = (user) => {
    const points = user.total_points || 0;
    if (points >= 1000) return { badge: '💎', name: 'Platinum', color: 'text-indigo-600' };
    if (points >= 500) return { badge: '🥇', name: 'Gold', color: 'text-yellow-600' };
    if (points >= 200) return { badge: '🥈', name: 'Silver', color: 'text-gray-600' };
    if (points >= 50) return { badge: '🥉', name: 'Bronze', color: 'text-amber-600' };
    return { badge: '🆕', name: 'Newcomer', color: 'text-green-600' };
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-6"></div>
          <div className="space-y-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
                </div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center">
        <div className="text-red-500 mb-4">⚠️</div>
        <p className="text-gray-600 dark:text-gray-400">{error}</p>
        <button
          onClick={loadLeaderboard}
          className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  const leaderboard = leaderboardData?.leaderboard || [];
  const currentUserStats = leaderboardData?.current_user_stats;
  const currentUserRank = leaderboardData?.current_user_rank;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <span className="text-3xl mr-2">🏆</span>
            Achievement Leaderboard
          </h2>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {leaderboardData?.total_participants?.length || 0} participants
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {/* Time Range Filter */}
          <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            {['all', 'month', 'week'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  timeRange === range
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {range === 'all' ? 'All Time' : range === 'month' ? 'This Month' : 'This Week'}
              </button>
            ))}
          </div>

          {/* Category Filter */}
          <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            {['points', 'achievements', 'streak'].map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  category === cat
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Current User Stats */}
        {currentUserStats && (
          <div className="bg-gradient-to-r from-orange-100 to-yellow-100 dark:from-orange-900 dark:to-yellow-900 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white font-bold">
                  {getRankIcon(currentUserStats.rank)}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white">Your Ranking</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Rank #{currentUserStats.rank} • {currentUserStats.total_points} points • {currentUserStats.total_achievements} achievements
                  </p>
                </div>
              </div>
              <div className="text-right">
                {(() => {
                  const badge = getAchievementBadge(currentUserStats);
                  return (
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{badge.badge}</span>
                      <span className={`text-sm font-medium ${badge.color}`}>{badge.name}</span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Leaderboard */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        {leaderboard.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">🏆</div>
            <p className="text-gray-600 dark:text-gray-400">No leaderboard data available yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leaderboard.map((participant, index) => {
              const rank = index + 1;
              const tierInfo = getTierInfo(rank);
              const badge = getAchievementBadge(participant);
              const isCurrentUser = participant.is_current_user;

              return (
                <div
                  key={participant.user_id}
                  className={`relative flex items-center space-x-4 p-4 rounded-lg transition-all duration-200 hover:shadow-md ${
                    isCurrentUser 
                      ? 'bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 ring-2 ring-orange-200 dark:ring-orange-700'
                      : rank <= 3
                      ? 'bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700'
                      : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  {/* Rank */}
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                    {rank <= 3 ? (
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${tierInfo.color} flex items-center justify-center text-white font-bold text-lg shadow-lg`}>
                        {getRankIcon(rank)}
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-300 font-bold">
                        #{rank}
                      </div>
                    )}
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white truncate">
                        {participant.user_name}
                        {isCurrentUser && <span className="ml-2 text-orange-500">(You)</span>}
                      </h4>
                      <div className="flex items-center space-x-1">
                        <span className="text-lg">{badge.badge}</span>
                        <span className={`text-xs ${badge.color} font-medium`}>{badge.name}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                      <span>🏆 {participant.total_achievements} achievements</span>
                      {participant.latest_achievement && (
                        <span>📅 Latest: {new Date(participant.latest_achievement).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>

                  {/* Points */}
                  <div className="flex-shrink-0 text-right">
                    <div className="text-xl font-bold text-gray-900 dark:text-white">
                      {participant.total_points.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">points</div>
                  </div>

                  {/* Tier Badge */}
                  {rank <= 10 && (
                    <div className="absolute top-2 right-2">
                      <div className={`px-2 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${tierInfo.color}`}>
                        {tierInfo.icon} {tierInfo.name}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Achievement Categories Stats */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          🏅 Top Performers by Category
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {['milestone', 'creative', 'engagement', 'social', 'streak'].map((cat) => {
            const topUser = leaderboard[0]; // Simplified - would need actual category leaders
            if (!topUser) return null;
            
            return (
              <div key={cat} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900 dark:text-white capitalize">{cat}</h4>
                  <span className="text-lg">
                    {cat === 'milestone' ? '🎯' : 
                     cat === 'creative' ? '🎨' : 
                     cat === 'engagement' ? '💪' : 
                     cat === 'social' ? '🤝' : '🔥'}
                  </span>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  👑 {topUser.user_name}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-500">
                  {topUser.total_points} points
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EnhancedLeaderboard;